import { Trash2, ClipboardList, Package, Plus } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useFinestraIndietro } from '../../utils/finestraIndietro';
import { confermaUscita } from '../../utils/uscitaProtetta';
import { visiteAPI, mediciAPI } from '../../services/api';
import { pacchettiAPI } from '../../services/fatturazioneApi';
import { imponibileRiga, ivaSu, somma } from '../../utils/denaro';
import { tracciabilitaAPI } from '../../services/tracciabilitaApi';
import ProceduraDettaglio from './ProceduraDettaglio';
import SceltaProcedura from './SceltaProcedura';
import DomandaProdottiUsati from './DomandaProdottiUsati';
import { PannelloRiservato, RichiesteInAttesa } from './AccessoRiservato';
import { useAuthStore } from '../../store/useStore';
import '../../styles/Visite.css';
import '../../styles/FirmaConsenso.css';
import { annunciaProcedureCambiate } from '../../utils/annunci';
import { notificaErrore, notificaAvviso, notificaConferma, messaggioDa } from '../../utils/notifiche';

/* Il nome dell'esecutore imposto, dall'elenco dei medici gia' caricato.
   Se l'elenco non e' ancora arrivato si mostra un ripiego, non un vuoto. */
const nomeMedico = (medici, id) => {
    const m = (medici || []).find((x) => String(x.id) === String(id));
    return m ? `${m.titolo ? m.titolo + ' ' : ''}${m.cognome || ''} ${m.nome || ''}`.trim() : null;
};

/**
 * Le visite e le procedure mediche del paziente.
 *
 * FASE 2 DEL RIORDINO (23 settembre 2026, decisioni di Alex):
 *  - una VISITA puo' contenere piu' PRESTAZIONI - visita, peeling,
 *    carbossiterapia - ognuna col suo riquadro dentro la visita;
 *  - «Nuova procedura» non sta piu' in testa alla pagina: e' il pulsante
 *    sotto il riquadro «Visita», e aggiunge una prestazione alla PROPRIA
 *    visita di OGGI (niente di retroattivo; la visita di un collega no);
 *  - in testa resta «Nuova visita», per quando il paziente non arriva
 *    dall'agenda: crea SUBITO la visita di oggi, VUOTA, col medico
 *    collegato, e la apre - si compila il riquadro «Visita», poi le
 *    prestazioni (la visita stessa compresa) da «Nuova procedura».
 *    Correzione di Alex del 23 settembre dopo la prova: prima «Nuova
 *    visita» apriva la scelta della prestazione. Se il medico ha gia' una
 *    visita oggi con quel paziente apre quella;
 *  - i due pulsanti li vede SOLO chi e' collegato a un medico: segreteria e
 *    amministrazione non registrano prestazioni, e il server lo rifiuta
 *    comunque. Il pulsante nascosto e' informazione, non protezione.
 */
const VisiteTab = ({ pazienteId, focusVisitaId, sessoPaziente }) => {
    const utente = useAuthStore((state) => state.user);
    const medico = !!(utente && utente.medico_id);

    // Procedure riservate aperte in questa sessione, con il dettaglio
    // completo restituito dal backend.
    const [visiteAperte, setVisiteAperte] = useState({});

    /* Le richieste di autorizzazione che riguardano questo paziente, dalle
       due parti: quelle che devo decidere io (ho eseguito la procedura) e
       quelle che ho chiesto io e aspettano risposta. Si caricano una volta
       e si indicizzano per visita: una chiamata invece di una per riga. */
    const [daDecidere, setDaDecidere] = useState({});
    const [mieInAttesa, setMieInAttesa] = useState({});

    const caricaRichieste = async () => {
        try {
            const r = await visiteAPI.richiesteAccesso();
            const perVisita = (righe, filtro) => {
                const mappa = {};
                (righe || []).filter(filtro).forEach((x) => {
                    mappa[x.visita_id] = [...(mappa[x.visita_id] || []), x];
                });
                return mappa;
            };
            setDaDecidere(perVisita(r.data?.data?.da_decidere, () => true));
            setMieInAttesa(perVisita(r.data?.data?.mie, (x) => x.stato === 'in_attesa'));
        } catch (e) {
            // Un medico senza autorizzazioni pendenti non deve vedere errori.
            setDaDecidere({});
            setMieInAttesa({});
        }
    };

    /* Dopo una decisione la riga sparisce dall'elenco senza ricaricare
       tutto: la procedura e' aperta a schermo e un ricaricamento la
       richiuderebbe sotto le mani di chi ha appena deciso. */
    const dopoDecisione = (richiestaId) => {
        setDaDecidere((p) => {
            const nuovo = {};
            Object.entries(p).forEach(([k, v]) => {
                const resto = v.filter((x) => x.id !== richiestaId);
                if (resto.length) nuovo[k] = resto;
            });
            return nuovo;
        });
    };

    /* Un elenco solo dal 22 settembre 2026 (fase 1 del riordino): le due
       cartelle erano una questione di menu, non di protezione. */
    const [visite, setVisite] = useState([]);
    /* La data di oggi la dice il server (ora italiana): la finestra la
       mostra, non la calcola col fuso del browser. */
    const [oggi, setOggi] = useState(null);
    const [medici, setMedici] = useState([]);
    const [loading, setLoading] = useState(true);
    /* LA VISITA APERTA STA NELL'INDIRIZZO (?visita=…, Alex 28/09/2026): cosi'
       «Indietro» la richiude e torna all'elenco, e ricaricare la riapre. */
    const [parametri, setParametri] = useSearchParams();
    const expandedVisita = parametri.get('visita') ? Number(parametri.get('visita')) : null;
    const setExpandedVisita = (id) => setParametri((p) => {
        const n = new URLSearchParams(p);
        if (id === null || id === undefined) n.delete('visita'); else n.set('visita', String(id));
        return n;
    });
    /* La scelta della prestazione aperta: { visita: null } per una visita
       nuova, { visita } per una procedura in piu' in quella visita. */
    const [scelta, setScelta] = useState(null);
    /* «Indietro» chiude la finestra della procedura e resta sulla visita. */
    useFinestraIndietro(!!scelta, () => { if (confermaUscita()) setScelta(null); });
    /* Vero quando la procedura appena salvata ha prodotti a lotto
       obbligatorio: si avvisa il medico di consegnare il tagliando, non
       gli si chiede il numero. */
    const [materialeInCoda, setMaterialeInCoda] = useState(false);
    const [moduliPerVisita, setModuliPerVisita] = useState({});
    const [prodottiPerVisita, setProdottiPerVisita] = useState({});
    /* I pacchetti che il medico collegato puo' continuare (Alex, 28/09/2026):
       il server li filtra - attivi, con sedute, non scaduti, voce che lui
       esegue. Se l'elenco e' vuoto «Continua pacchetto» non compare. */
    const [pacchettiAperti, setPacchettiAperti] = useState([]);
    const caricaPacchetti = async () => {
        if (!utente?.medico_id) return;
        try {
            const r = await pacchettiAPI.aperti(pazienteId);
            setPacchettiAperti(r.data?.data?.pacchetti || []);
        } catch (e) { setPacchettiAperti([]); }
    };

    const loadVisite = async () => {
        try {
            setLoading(true);
            const response = await visiteAPI.getByPazienteId(pazienteId);
            if (response.data.success) {
                setVisite(response.data.data.visite);
                setOggi(response.data.data.oggi || null);
            }
        } catch (error) {
            console.error('Errore caricamento procedure:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadMedici = async () => {
        try {
            const response = await mediciAPI.getAll(true);
            if (response.data.success) setMedici(response.data.data.medici || []);
        } catch (error) {
            console.error('Errore caricamento medici:', error);
        }
    };

    const loadModuli = async (visitaId) => {
        try {
            const response = await visiteAPI.getModuli(visitaId);
            if (response.data.success) {
                setModuliPerVisita((prev) => ({ ...prev, [visitaId]: response.data.data.moduli }));
                /* I prodotti delle procedure iniettabili arrivano coi moduli
                   (filler, migration 324): una lettura sola. */
                setProdottiPerVisita((prev) => ({ ...prev, [visitaId]: {
                    righe: response.data.data.prodotti_procedura || [],
                    procedure: response.data.data.procedure || {},
                } }));
            }
        } catch (error) {
            console.error('Errore caricamento moduli:', error);
        }
    };

    /* Una visita aperta dall'indirizzo (ricaricando, o con «Avanti»): i
       suoi moduli si caricano come col clic. */
    useEffect(() => {
        if (expandedVisita && !moduliPerVisita[expandedVisita]) loadModuli(expandedVisita);
        // eslint-disable-next-line
    }, [expandedVisita]);

    useEffect(() => {
        loadVisite();
        loadMedici();
        caricaRichieste();
        caricaPacchetti();
        // eslint-disable-next-line
    }, [pazienteId]);

    const apri = (visitaId) => {
        setExpandedVisita(visitaId);
        loadModuli(visitaId);
        setTimeout(() => {
            const el = document.getElementById('visita-' + visitaId);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 150);
    };

    // apertura automatica di una procedura richiesta da Timeline/Recap
    useEffect(() => {
        if (focusVisitaId && !loading) apri(focusVisitaId);
        // eslint-disable-next-line
    }, [focusVisitaId, loading]);

    /* «Nuova visita»: la visita di oggi nasce subito, vuota, e si apre.
       Se oggi questo medico ha gia' una visita con il paziente, si apre
       quella - lo dice il server (409 con il suo id), non un conto fatto
       qui su un elenco che potrebbe essere vecchio. */
    const [creando, setCreando] = useState(false);
    /* La domanda sui prodotti del cestino: { visitaId, prodotti } (291). */
    const [domandaProdotti, setDomandaProdotti] = useState(null);
    const [cancellando, setCancellando] = useState(false);
    useFinestraIndietro(!!domandaProdotti, () => { if (!cancellando) setDomandaProdotti(null); });
    const nuovaVisita = async () => {
        if (creando) return;
        setCreando(true);
        try {
            const r = await visiteAPI.create(pazienteId, {});
            const nata = r.data?.data?.visita;
            await loadVisite();
            annunciaProcedureCambiate();
            if (nata) apri(nata.id);
        } catch (e) {
            if (e?.response?.status === 409 && e?.response?.data?.data?.visita_id) {
                await visitaEsistente(e.response.data.data.visita_id, messaggioDa(e));
            } else {
                notificaErrore(messaggioDa(e, 'Non riesco a creare la visita'));
            }
        } finally {
            setCreando(false);
        }
    };

    const dopoCreazione = async (visita) => {
        setScelta(null);
        caricaPacchetti();
        await loadVisite();
        annunciaProcedureCambiate();
        if (!visita) return;
        apri(visita.id);
        /* Se la procedura ha prodotti a lotto obbligatorio si avvisa,
           una volta, che il tagliando va consegnato. Non si blocca
           niente: la coda del magazzino ha comunque la riga. */
        try {
            const r = await tracciabilitaAPI.daIndicare(visita.id);
            setMaterialeInCoda((r.data.data.da_indicare || []).length > 0);
        } catch (e) { /* l'avviso e' una cortesia, non un passaggio */ }
    };

    /* Il server ha risposto che la visita di oggi c'e' gia': si apre
       quella. */
    const visitaEsistente = async (visitaId, messaggio) => {
        setScelta(null);
        await loadVisite();
        apri(visitaId);
        if (messaggio) notificaAvviso(messaggio);
    };

    const handleToggleVisita = (visitaId) => {
        if (expandedVisita === visitaId) {
            setExpandedVisita(null);
        } else {
            setExpandedVisita(visitaId);
            if (!moduliPerVisita[visitaId]) loadModuli(visitaId);
        }
    };

    /* Un modulo nasce nel riquadro di UNA prestazione (fase 2): porta con
       se' la riga a cui appartiene. */
    /* La galleria delle foto di una prestazione nasce al primo caricamento
       (ModuloFoto incorporato): si crea e basta, SENZA ricaricare i moduli,
       perche' la foto si sta ancora caricando - la ricarica la fa lui alla
       fine. */
    const creaModulo = async (visitaId, tipo, visitaPrestazioneId) => {
        const r = await visiteAPI.createModulo(visitaId, {
            tipo,
            titolo: tipo === 'foto' ? 'Fotografie' : tipo,
            contenuto: tipo === 'foto' ? { foto: [], descrizione: '' } : {},
            ordine: (moduliPerVisita[visitaId]?.length || 0),
            visita_prestazione_id: visitaPrestazioneId,
        });
        const modulo = r.data?.data?.modulo;
        if (!modulo) throw new Error('Il server non ha restituito la galleria');
        return modulo;
    };

    const handleAddModulo = async (visitaId, tipo, visitaPrestazioneId = null) => {
        const modulo = {
            tipo,
            titolo: tipo.charAt(0).toUpperCase() + tipo.slice(1) + ' - ' + new Date().toLocaleDateString('it-IT'),
            contenuto: {},
            ordine: (moduliPerVisita[visitaId]?.length || 0),
            visita_prestazione_id: visitaPrestazioneId,
        };
        try {
            await visiteAPI.createModulo(visitaId, modulo);
            loadModuli(visitaId);
        } catch (error) {
            notificaErrore(messaggioDa(error, 'Non riesco a creare il modulo'));
        }
    };

    /* IL CESTINO DELLE VISITE (migration 291, proposta 25): «Elimina» mette
       la visita nel cestino, da dove l'amministrazione la ripristina. Se dalla
       visita sono usciti prodotti dal magazzino il server risponde 409 con la
       domanda (risposta 2 di Alex: niente reso automatico), e la si fa qui.
       I rifiuti - fattura, seduta eseguita, referto concluso, patologia nata
       dalla visita - arrivano col motivo del server.
       DOPO LA CHIUSURA DEL RIQUADRO «VISITA» (migration 320, la visita
       scritta sul paziente sbagliato): la mette nel cestino solo
       l'amministrazione, col motivo scritto. Chi e quando lo decide il
       database; la domanda del motivo la fa il server (409, domanda
       'motivo') e qui si chiede con le sue parole. Il motivo resta con la
       visita anche se poi arriva la domanda sui prodotti. */
    const cancellaVisita = async (id, prodottiUsati, motivo) => {
        setCancellando(true);
        try {
            const r = await visiteAPI.delete(id, prodottiUsati, motivo);
            setDomandaProdotti(null);
            notificaConferma(r.data?.message || 'Visita messa nel cestino.');
            loadVisite(); annunciaProcedureCambiate();
        } catch (error) {
            const d = error?.response?.data;
            if (error?.response?.status === 409 && d?.data?.domanda === 'motivo') {
                setDomandaProdotti(null);
                const scritto = window.prompt(d.message || 'Scrivi il motivo.', motivo || '');
                if (scritto !== null) await cancellaVisita(id, prodottiUsati, scritto);
            } else if (error?.response?.status === 409 && d?.data?.domanda === 'prodotti_usati') {
                setDomandaProdotti({ visitaId: id, prodotti: d.data.prodotti || [], motivo });
            } else {
                setDomandaProdotti(null);
                notificaErrore(messaggioDa(error, 'Non riesco a mettere la visita nel cestino'));
            }
        } finally {
            setCancellando(false);
        }
    };

    const handleDeleteVisita = async (id) => {
        if (window.confirm('Mettere questa visita nel cestino? Sparisce dalla scheda con le sue prestazioni, '
            + 'i moduli, le fotografie e i documenti; l\'amministrazione può ripristinarla dal Cestino.')) {
            await cancellaVisita(id);
        }
    };

    const formatEuro = (v) => {
        const n = parseFloat(v);
        if (isNaN(n)) return '€ 0,00';
        return n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
    };

    const calcTotaleVisita = (prestazioni) => {
        if (!Array.isArray(prestazioni)) return 0;
        // Stessa aritmetica della fattura: due punti che rispondono alla
        // stessa domanda devono dare lo stesso numero.
        return prestazioni.reduce((sum, p) => {
            const riga = imponibileRiga(p.prezzo_unitario, parseFloat(p.quantita) || 0);
            return somma(sum, riga, ivaSu(riga, p.iva));
        }, 0);
    };

    if (loading && !visite.length) {
        return <div className="loading">Caricamento procedure...</div>;
    }

    return (
        <div className="visite-container">
            <div className="visite-header">
                <h2>Visite e procedure mediche</h2>
                {medico && (
                    <button onClick={nuovaVisita} className="btn-primary" disabled={creando}
                            title="La visita di oggi, vuota: si compila «Visita», poi le prestazioni da «Nuova procedura».">
                        <Plus size={15} aria-hidden="true" /> Nuova visita
                    </button>
                )}
            </div>

            {/* IL LOTTO NON SI CHIEDE QUI, E NON E' UNA SEMPLIFICAZIONE
                Chi ha la scatola in mano ha anche il paziente sul lettino:
                fermarsi a digitare un codice a dodici cifre in quel momento
                significa digitarlo di fretta. Il medico consegna il
                tagliando alla segreteria, che lo registra dalla coda del
                magazzino leggendolo con calma. */}
            {materialeInCoda && (
                <div className="vt-avviso-materiale">
                    <Package size={16} />
                    <div>
                        <b>Materiale tracciato da scaricare.</b>{' '}
                        Consegna il tagliando con il numero di lotto alla segreteria:
                        lo registra lei dalla coda del magazzino. La procedura è già
                        salvata e il prodotto risulta usato.
                        <button className="vt-chiudi-avviso" onClick={() => setMaterialeInCoda(false)}>
                            Ho capito
                        </button>
                    </div>
                </div>
            )}

            {visite.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-icon"><ClipboardList size={38} strokeWidth={1.2} aria-hidden="true" /></div>
                    <h3>Nessuna visita</h3>
                    <p>{medico
                        ? 'Clicca «Nuova visita» per iniziare.'
                        : 'Le visite le registra il medico che le esegue.'}</p>
                </div>
            ) : (
                <div className="visite-list">
                    {visite.map((visita) => {
                        const prestList = visita.prestazioni || [];
                        const totaleVis = calcTotaleVisita(prestList);
                        return (
                            <div key={visita.id} id={`visita-${visita.id}`} className="visita-card">
                                <div className="visita-row">
                                    <div className="visita-info">
                                        <div className="visita-data">
                                            {new Date(visita.data_visita).toLocaleDateString('it-IT')}
                                            {visita.di_oggi && <span className="visita-oggi">oggi</span>}
                                        </div>
                                        <div className="visita-dettagli">
                                            <span className="visita-patologia">{visita.patologia}</span>
                                            {visita.medico_cognome && (
                                                <>
                                                    <span className="visita-separator">•</span>
                                                    <span className="visita-medico">Dr. {visita.medico_cognome} {visita.medico_nome}</span>
                                                </>
                                            )}
                                            {prestList.length === 0 && visita.trattamento && (
                                                <>
                                                    <span className="visita-separator">•</span>
                                                    <span className="visita-trattamento">{visita.trattamento}</span>
                                                </>
                                            )}
                                            {/* Una visita senza prestazioni non va in cassa:
                                                l'elenco lo dice, invece di mostrarla come
                                                una visita qualunque a zero euro. */}
                                            {prestList.length === 0 && !visita.riservata_nascosta && (
                                                <>
                                                    <span className="visita-separator">•</span>
                                                    <span className="visita-senza-prestazioni">senza prestazioni</span>
                                                </>
                                            )}
                                        </div>
                                        {prestList.length > 0 && (
                                            <div className="visita-prestazioni-inline">
                                                {prestList.map((p, idx) => (
                                                    <span key={p.id || idx} className="prest-chip">
                                                        {p.nome_snapshot}
                                                        {parseFloat(p.quantita) !== 1 && <span className="prest-qta"> ×{p.quantita}</span>}
                                                    </span>
                                                ))}
                                                <span className="visita-totale">Totale: <strong>{formatEuro(totaleVis)}</strong></span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="visita-actions">
                                        <button onClick={() => handleToggleVisita(visita.id)} className="btn-expand">
                                            {expandedVisita === visita.id ? '▼' : '▶'}
                                        </button>
                                        <button onClick={() => handleDeleteVisita(visita.id)} className="btn-delete-small" title="Elimina: la visita va nel cestino"><Trash2 size={15} strokeWidth={1.8} aria-hidden="true" /></button>
                                    </div>
                                </div>

                                {expandedVisita === visita.id && (
                                    <>
                                        {/* Le richieste da decidere si vedono anche
                                            quando la procedura e' aperta. */}
                                        <RichiesteInAttesa
                                            richieste={daDecidere[visita.id]}
                                            onDecisa={dopoDecisione}
                                        />
                                        {visita.riservata_nascosta && !visiteAperte[visita.id] ? (
                                            <PannelloRiservato
                                                visita={visita}
                                                richiestaInAttesa={(mieInAttesa[visita.id] || [])[0]}
                                                onRichiesta={(r) => setMieInAttesa(
                                                    (p) => ({ ...p, [visita.id]: [r] }))}
                                                onAperta={(v) => setVisiteAperte(
                                                    (p) => ({ ...p, [visita.id]: v }))}
                                            />
                                        ) : (
                                            <ProceduraDettaglio
                                                sessoPaziente={sessoPaziente}
                                                visita={visiteAperte[visita.id] || visita}
                                                moduli={moduliPerVisita[visita.id] || []}
                                                prodotti={prodottiPerVisita[visita.id]}
                                                onAddModulo={handleAddModulo}
                                                onCreaModulo={creaModulo}
                                                onReload={() => loadModuli(visita.id)}
                                                utente={utente}
                                                /* Il pulsante sotto «Visita»: un medico,
                                                   la SUA visita, di oggi, clinica. Le stesse
                                                   regole le fa rispettare il server
                                                   (rifiutoProcedura): la visita di un
                                                   collega non riceve procedure, perche' la
                                                   riservatezza e' del medico della visita. */
                                                puoAggiungere={medico && visita.di_oggi === true
                                                    && visita.tipo === 'clinica'
                                                    && Number(visita.medico_id) === Number(utente.medico_id)}
                                                onNuovaProcedura={() => setScelta({ visita })}
                                                pacchettiAperti={pacchettiAperti}
                                                onContinuaPacchetto={(pacchetto) => setScelta({ visita, pacchetto })}
                                                onPrestazioniCambiate={() => { loadVisite(); caricaPacchetti(); }}
                                            />
                                        )}
                                    </>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {domandaProdotti && (
                <DomandaProdottiUsati
                    prodotti={domandaProdotti.prodotti}
                    inCorso={cancellando}
                    onRisposta={(usati) => cancellaVisita(domandaProdotti.visitaId, usati, domandaProdotti.motivo)}
                    onAnnulla={() => { if (!cancellando) setDomandaProdotti(null); }}
                />
            )}

            {scelta && (
                <SceltaProcedura
                    pazienteId={pazienteId}
                    visita={scelta.visita}
                    oggi={oggi}
                    utente={utente}
                    nomeEsecutore={nomeMedico(medici, utente?.medico_id)}
                    pacchetto={scelta.pacchetto || null}
                    pacchettiAperti={pacchettiAperti}
                    onUsaPacchetto={(pacchetto) => setScelta((s) => ({ ...s, pacchetto }))}
                    onCreata={dopoCreazione}
                    onChiudi={() => setScelta(null)}
                />
            )}
        </div>
    );
};

export default VisiteTab;
